import { AppRoute } from "app";
import { useAtomValue } from "jotai";
import { useCallback, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import { appFlowAtom, flowWithProgressStepperAtom } from "atoms/appFlow";
import { clientConfigAtom } from "atoms/clientConfig";
import { sessionIdAtom } from "atoms/sessionId";
import { tosAcceptedAtom } from "atoms/tosAccepted";
import { Event } from "types/Event";
import { replaceSessionInAppRoute } from "utils/routeHelper";

import { useClientTranslation } from "./useClientTranslation";
import { useEventTracking } from "./useEventTracking";
import { serverSessionAtom } from "atoms/serverSession";

type PageRoutingProps = {
  onNext: () => void;
  nextRoute: AppRoute;
  currentProgressStep: number;
  continueCTA: string;
  onTrackAndNext: (event: Event) => () => void;
  navigateTo: (route: AppRoute) => void;
};

export const usePageRouting = (): PageRoutingProps => {
  const navigate = useNavigate();
  const sessionId = useAtomValue(sessionIdAtom);

  const { pathname, state } = useLocation();

  const { trackEvent } = useEventTracking();

  const { t } = useClientTranslation();

  const appFlow = useAtomValue(appFlowAtom);
  const appFlowPaths = appFlow.map(({ path }) => path);
  const flowWithStepper = useAtomValue(flowWithProgressStepperAtom);
  const flowWithStepperPaths = flowWithStepper.map(({ path }) => path);

  // This gets the route from /sessionid/path -> path
  const route = pathname.replace(sessionId, ":session") as AppRoute;
  const matchingNextRoute = appFlow[appFlowPaths.indexOf(route) + 1]?.path || route;
  const nextRoute = replaceSessionInAppRoute(matchingNextRoute, sessionId) as AppRoute;

  const currentProgressStep = flowWithStepperPaths.indexOf(route) + 1;
  const continueCTA = state === "FROM_SUMMARY" ? t("global.save_edits") : t("global.continue");

  const {
    welcome: { termsOfUseModal },
    speedyPayment,
  } = useAtomValue(clientConfigAtom);
  const tosAccepted = useAtomValue(tosAcceptedAtom);
  const serverSession = useAtomValue(serverSessionAtom);
  const hasPayeeEnabledInSession = !!serverSession?.enablePayeeJourney;

  const onNext = useCallback(() => {
    if (state === "FROM_SUMMARY") {
      navigate(replaceSessionInAppRoute(AppRoute.ClaimSummary, sessionId), {
        replace: true,
      });
    } else {
      navigate(nextRoute);
    }
  }, [navigate, sessionId, nextRoute, state]);

  const onTrackAndNext = (event: Event) => () => {
    trackEvent(event);
    onNext();
  };

  const navigateTo = (route: AppRoute) => {
    navigate(replaceSessionInAppRoute(route, sessionId))
  }

  useEffect(() => {
    if (!termsOfUseModal || tosAccepted) return;

    if (!tosAccepted && route !== AppRoute.Welcome && !route.includes("error")) {
      if (hasPayeeEnabledInSession) {
        navigate(
          replaceSessionInAppRoute(
            speedyPayment?.showRepairablePage ? AppRoute.EstimateRepariablePayee : AppRoute.SpeedyPayment,
            sessionId
          )  
        );
      } else {
        navigate(replaceSessionInAppRoute(AppRoute.Welcome, sessionId));
      }
    }
  }, [navigate, route, sessionId, termsOfUseModal, tosAccepted, hasPayeeEnabledInSession]);

  return {
    onNext,
    nextRoute,
    currentProgressStep,
    continueCTA,
    onTrackAndNext,
    navigateTo,
  };
};
